import { useUpdateCircleRecommendationStatus } from '@/hooks/use-update-circle-recommendation-status';
import { useAnalyticsEventProps } from '@/libs/analytics/event-props-provider';
import { logger } from '@/libs/logger';
import { getGuideId } from '@/utils/media';
import { EventProperties } from '@calm/evexia-analytics';
import { RbcStatus } from '@calm/evexia-web-sdk';
import { useGuidePlayer } from '@rhg/hooks/use-guide-player';
import { useProvidedProgramContextWithGuidesAndWorkflow } from '@rhg/hooks/use-provided-program-context/with-guides-and-workflow';
import { getProgramRoute } from '@rhg/utils/programs';
import { useRouter } from 'next/router';
import { useCallback, useMemo, useRef } from 'react';

/**
 * Returns a callback that opens the guide player on the current program's
 * next/in-progress session, marks the recommendation as Viewed, and
 * optionally navigates to the program details page.
 *
 * Must be used inside a ProgramProvider. Safe to call when the context is
 * empty — the returned callback will no-op the play/update steps if no
 * session/program is available, but will still navigate when requested.
 *
 * The returned callback resolves once navigation completes (or immediately
 * when `shouldNavigate` is `false`), and rejects when navigation fails or
 * cannot be performed. Callers may attach `.catch` to recover from the
 * "navigation never happened" case (e.g. clearing a loading flag).
 */
export function useProgramCardPlayHandler(trackProps?: EventProperties) {
    const { openGuidePlayer } = useGuidePlayer();
    const {
        program,
        recommendation,
        packClass,
        workflow,
        currentSession,
        toolsByGuide,
    } = useProvidedProgramContextWithGuidesAndWorkflow();
    const { push, query } = useRouter();
    const { updateStatusAsync } = useUpdateCircleRecommendationStatus(
        recommendation?.recDefnId
    );

    const trackPropsRef = useRef(trackProps);
    trackPropsRef.current = trackProps;

    // Feed pages only expose their FeedID / FeedLayout / FeedHasTags / TagName
    // via an ambient AnalyticsEventPropsProvider, and Session : Started fires
    // later from the guide player (rendered outside that subtree) — so those
    // page-level feed props can't reach it any other way. Snapshot them here
    // at play time, at lowest precedence (explicit trackProps still wins).
    // Mirrors useSessionCardPlayHandler's identical guide-card treatment.
    const { FeedID, FeedLayout, FeedHasTags, IsFeaturedFeed, TagName } =
        useAnalyticsEventProps();
    const feedTrackProps = useMemo<EventProperties>(
        () =>
            Object.fromEntries(
                Object.entries({
                    FeedID,
                    FeedLayout,
                    FeedHasTags,
                    IsFeaturedFeed,
                    TagName,
                }).filter(([, value]) => value !== undefined)
            ),
        [FeedID, FeedLayout, FeedHasTags, IsFeaturedFeed, TagName]
    );

    const detailsRoute = useMemo(() => {
        const additionalParams: Record<string, string> = {};
        if (query.category && typeof query.category === 'string') {
            additionalParams.category = query.category;
        }
        return getProgramRoute(program, packClass, 'true', additionalParams);
    }, [packClass, program, query.category]);

    return useCallback(
        ({ shouldNavigate }: { shouldNavigate: boolean }): Promise<void> => {
            if (currentSession && packClass && program) {
                updateStatusAsync({ status: RbcStatus.Viewed }).catch((err) =>
                    logger.error(
                        'Failed to mark recommendation as viewed',
                        {},
                        err as Error
                    )
                );
                openGuidePlayer({
                    guide: currentSession,
                    packClass,
                    workflow,
                    tools: toolsByGuide[getGuideId(currentSession)],
                    willSkipToPreviousProgress: true,
                    sessionTrackProps: {
                        ...feedTrackProps,
                        ...trackPropsRef.current,
                    },
                }).catch((err) =>
                    logger.error(
                        'Failed to open guide player',
                        {},
                        err as Error
                    )
                );
            }
            if (!shouldNavigate) {
                return Promise.resolve();
            }
            if (!detailsRoute) {
                return Promise.reject(
                    new Error(
                        'useProgramCardPlayHandler: no program details route resolved'
                    )
                );
            }
            return push(detailsRoute).then(() => undefined);
        },
        [
            currentSession,
            packClass,
            program,
            openGuidePlayer,
            workflow,
            toolsByGuide,
            detailsRoute,
            push,
            updateStatusAsync,
            feedTrackProps,
        ]
    );
}
